<template>
  <!-- 内嵌模式：不弹窗，直接渲染在父组件中 -->
  <div v-if="embedded">
    <!-- 预览模式 -->
    <template v-if="!designerVisible">
      <div class="print-toolbar" style="display:flex; align-items:center; gap:8px; margin-bottom:12px;">
        <a-button key="templateSetting" icon="setting" :disabled="autoPrint" @click="handleTemplateSetting">模板设置</a-button>
        <a-button key="printPreview" type="primary" icon="printer" @click="handlePrint">打印</a-button>
        <a-button key="exportPdf" icon="file-pdf" @click="handleExportPdf">导出PDF</a-button>
        <a-checkbox v-model="autoPrint" @change="onAutoPrintChange">自动</a-checkbox>
      </div>
      <a-spin :spinning="loading" tip="正在加载模板...">
        <div v-if="!templateJson && !loading" class="no-template">
          <a-empty description="暂无打印模板" />
        </div>
        <div v-else class="preview-wrapper">
          <div ref="printContent" class="print-content"></div>
        </div>
      </a-spin>
    </template>
    <!-- 设计器模式（内嵌） -->
    <div v-if="designerVisible" class="designer-wrapper embedded">
      <EmbeddedDesigner
        ref="embeddedDesigner"
        :billType="billType"
        :billTypeName="billTypeName"
        :billData="currentPrintData"
        @close="onDesignerClose"
        @saved="onDesignerSaved"
      />
    </div>
  </div>

  <!-- 弹窗模式（兼容） -->
  <a-modal
    v-else
    :title="'打印预览 - ' + billTypeName"
    :width="900"
    :visible="visible"
    :maskClosable="false"
    :forceRender="true"
    :footer="null"
    fullscreen
    switchFullscreen
    @cancel="handleCancel"
    wrapClassName="report-print-modal"
  >
    <!-- 预览模式 -->
    <template v-if="!designerVisible">
      <div class="print-toolbar" style="display:flex; align-items:center; gap:8px; margin-bottom:12px;">
        <a-button key="templateSetting" icon="setting" :disabled="autoPrint" @click="handleTemplateSetting">模板设置</a-button>
        <a-button key="printPreview" type="primary" icon="printer" @click="handlePrint">打印</a-button>
        <a-button key="exportPdf" icon="file-pdf" @click="handleExportPdf">导出PDF</a-button>
        <a-checkbox v-model="autoPrint" @change="onAutoPrintChange">自动</a-checkbox>
      </div>
      <a-spin :spinning="loading" tip="正在加载模板...">
        <div v-if="!templateJson && !loading" class="no-template">
          <a-empty description="暂无打印模板" />
        </div>
        <div v-else class="preview-wrapper">
          <div ref="printContent" class="print-content"></div>
        </div>
      </a-spin>
    </template>
    <!-- 设计器模式 -->
    <div v-if="designerVisible" class="designer-wrapper">
      <EmbeddedDesigner
        ref="embeddedDesigner"
        :billType="billType"
        :billTypeName="billTypeName"
        :billData="currentPrintData"
        @close="onDesignerClose"
        @saved="onDesignerSaved"
      />
    </div>
  </a-modal>
</template>

<script>
import EmbeddedDesigner from './EmbeddedDesigner.vue'
import { loadTemplateWithFallback, getCurrentSystemInfo } from '@/api/plugin/print-template'

export default {
  name: 'ReportPrint',
  components: { EmbeddedDesigner },
  props: {
    visible: { type: Boolean, default: false },
    tableData: { type: [Array, Object], default: () => [] },
    queryParam: { type: Object, default: () => ({}) },
    billType: { type: String, required: true },
    billTypeName: { type: String, required: true },
    buildData: { type: Function, required: true },
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      templateJson: null,
      autoPrint: false,
      systemAutoPrint: false,
      companyInfo: {},
      userInfo: {},
      designerVisible: false,
      currentPrintData: null
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) this.init()
      }
    }
  },
  methods: {
    async init() {
      this.loading = true
      try {
        const store = this.$store || (window.Vue && window.Vue.prototype.$store)
        if (store && store.getters && store.getters.userInfo) {
          this.userInfo = store.getters.userInfo
        } else if (store && store.state && store.state.user && store.state.user.info) {
          this.userInfo = store.state.user.info
        }
        const tenantId = this.userInfo ? this.userInfo.tenantId : null

        const [templateResult, systemRes] = await Promise.all([
          loadTemplateWithFallback(this.billType, tenantId),
          getCurrentSystemInfo()
        ])

        if (templateResult && templateResult.templateJson && templateResult.templateJson.panels
            && templateResult.templateJson.panels.length) {
          this.templateJson = templateResult.templateJson
        } else {
          console.warn('[ReportPrint] 未找到打印模板:', this.billType)
          return
        }

        if (systemRes && systemRes.code === 200 && systemRes.data) {
          this.companyInfo = systemRes.data
          if (systemRes.data.autoPrint !== undefined && systemRes.data.autoPrint !== null) {
            this.autoPrint = (systemRes.data.autoPrint === '1')
            this.systemAutoPrint = (systemRes.data.autoPrint === '1')
          }
        }

        this.renderTemplate()

        this.$nextTick(() => {
          if (this.systemAutoPrint || this.autoPrint) {
            setTimeout(() => { this.handlePrint() }, 800)
          }
        })
      } catch (error) {
        console.error('[ReportPrint] 初始化失败:', error)
      } finally {
        this.loading = false
      }
    },

    renderTemplate() {
      if (!this.templateJson || !this.$refs.printContent) return
      const hiprint = window.hiprint
      if (!hiprint || !hiprint.PrintTemplate) {
        console.error('[ReportPrint] hiprint 未就绪')
        return
      }
      const printData = this.buildData(this.tableData, this.queryParam, this.userInfo, this.companyInfo)
      this.autoComputeTotals(printData)
      const template = new hiprint.PrintTemplate({ template: this.templateJson })
      const htmlElements = template.getHtml(printData)
      if (htmlElements && htmlElements[0]) {
        this.$refs.printContent.innerHTML = htmlElements[0].innerHTML
      }
    },

    /**
     * 自动计算模板中 total_ 前缀字段的合计值
     * 规则：total_<columnName> → 对 table 中 <columnName> 列求和
     */
    autoComputeTotals(printData) {
      if (!this.templateJson || !printData || !printData.table) return

      const tableData = printData.table || []
      const panels = this.templateJson.panels || []

      panels.forEach(panel => {
        const elements = panel.printElements || []
        elements.forEach(el => {
          const elType = el.printElementType
          if (!elType || elType.type !== 'text') return

          const field = el.options && el.options.field
          if (!field || !field.startsWith('total_')) return

          const columnName = field.replace('total_', '')
          let sum = 0
          let found = false
          tableData.forEach(row => {
            if (row.rowIndex === '合计') return
            if (row[columnName] !== undefined) {
              found = true
              const val = parseFloat(row[columnName])
              if (!isNaN(val)) sum += val
            }
          })
          if (found) {
            printData[field] = sum
          }
        })
      })
    },

    handlePrint() {
      if (!this.$refs.printContent || !this.$refs.printContent.innerHTML) {
        this.$message.warning('暂无打印内容')
        return
      }
      const htmlContent = this.$refs.printContent.innerHTML
      const printWindow = window.open('', '_blank', 'width=900,height=600')
      if (!printWindow) {
        this.$message.warning('请允许浏览器弹出窗口')
        return
      }
      printWindow.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + this.billTypeName + '</title><link rel="stylesheet" type="text/css" media="print" href="' + window.location.origin + '/print-lock.css"><style>@page{margin:0}@media print{body{margin:0;padding:0}}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>' + htmlContent + '</body></html>')
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }
    },

    handleExportPdf() {
      if (!this.templateJson) {
        this.$message.warning('暂无模板数据')
        return
      }
      const hiprint = window.hiprint
      if (!hiprint || !hiprint.PrintTemplate) {
        this.$message.error('打印引擎未就绪')
        return
      }
      const printData = this.buildData(this.tableData, this.queryParam, this.userInfo, this.companyInfo)
      this.autoComputeTotals(printData)
      const fileName = this.billTypeName + '_' + new Date().toISOString().slice(0, 10)
      const tempTemplate = new hiprint.PrintTemplate({ template: this.templateJson })
      tempTemplate.toPdf(printData, fileName, { scale: 2, useCORS: true })
    },

    handleTemplateSetting() {
      this.currentPrintData = this.buildData(this.tableData, this.queryParam, this.userInfo, this.companyInfo)
      this.autoComputeTotals(this.currentPrintData)
      this.designerVisible = true
      this.$emit('designer-change', true)
    },

    onDesignerClose() {
      this.designerVisible = false
      this.$emit('designer-change', false)
    },

    onDesignerSaved() {
      this.designerVisible = false
      this.$emit('designer-change', false)
      this.$message.success('模板保存成功，正在刷新预览...')
      this.init()
    },

    handleCancel() {
      this.autoPrint = false
      this.$emit('update:visible', false)
    },

    onAutoPrintChange(checked) {
      if (this.systemAutoPrint && !checked) {
        this.$message.info('全局自动打印已开启，不可关闭')
        this.autoPrint = true
      }
    }
  }
}
</script>

<style scoped>
.report-print-modal >>> .ant-modal { top: 20px; height: 95%; }
.report-print-modal >>> .ant-modal-body { height: calc(100% - 55px); overflow: auto; }
.preview-wrapper { background: #fff; min-height: 400px; }
.print-content { background: #fff; }
.no-template { padding: 80px 0; text-align: center; }
.designer-wrapper { width: 100%; height: calc(100vh - 200px); }
.designer-wrapper.embedded { height: calc(100vh - 160px); }
</style>